import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Paper,
  Divider,
  Alert,
  Snackbar,
  Link,
  Button,
  useTheme
} from '@mui/material';
import ChatMessageList from './ChatMessageList';
import ChatInput from './ChatInput';
import { useAuth } from '../../context/AuthContext';
import { useWebSocketChat } from '../../hooks';
import { webSocketService, chatApi } from '../../services';
import LoginIcon from '@mui/icons-material/Login';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { IconButton } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

/**
 * ChatContainer component
 * 
 * A reusable chat container that manages the chat interface and WebSocket communication
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Title to display at the top of the chat container
 * @param {string} props.welcomeMessage - Welcome message to show when chat is initialized
 * @param {string} props.selectedQuestion - Question to pre-fill in the chat input
 * @param {Function} props.onQuestionUsed - Callback when the selected question has been used
 * @returns {JSX.Element} Rendered component
 */
const ChatContainer = ({ title, welcomeMessage, selectedQuestion, onQuestionUsed }) => {
  const { currentUser } = useAuth();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { 
    messages, 
    isConnected, 
    error, 
    sendMessage, 
    clearError
    , isSending
  } = useWebSocketChat(webSocketService, chatApi, welcomeMessage);
  const navigate = useNavigate();
  const [showConnectionStatus, setShowConnectionStatus] = React.useState(false);

  // Show connection status when user connects
  React.useEffect(() => {
    if (currentUser && isConnected) {
      setShowConnectionStatus(true);
    }
  }, [currentUser, isConnected]);
  
  const handleLogin = () => {
    navigate('/login');
  };

  const handleCloseConnectionStatus = () => {
    setShowConnectionStatus(false);
  };

  const handleToggleFullScreen = () => setIsFullScreen(prev => !prev);
  
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      ...(isFullScreen && {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        zIndex: theme.zIndex.modal,
        bgcolor: 'background.default'
      })
    }}>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, position: 'relative' }}>
          <Typography variant="h4" component="h2" sx={{ color: 'text.secondary' }}>
            {isSmallScreen ? 'Agente Ualabi' : title}
          </Typography>
          {isSmallScreen && (
            <IconButton
              onClick={handleToggleFullScreen}
              aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
              sx={{ position: 'absolute', right: theme.spacing(2) }}
            >
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}
        </Box>
      )}
      
      <Paper elevation={3} sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}>
        {/* Message area */}
        <ChatMessageList 
          messages={messages}
        />
        
        <Divider />
        
        {/* Chat input area */}
        <Box sx={{
          p: 2,
          bgcolor: 'background.paper'
        }}>
          {!currentUser ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Para enviar mensajes y utilizar el chat en tiempo real:
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<LoginIcon />}
                onClick={handleLogin}
                sx={{ mt: 1, px: 3 }}
              >
                Iniciar sesión
              </Button>
            </Box>
          ) : (
            <ChatInput 
              onSendMessage={sendMessage} 
              isDisabled={isSending || !currentUser || (!isConnected && messages.length > 5)}
              selectedQuestion={selectedQuestion}
              onQuestionUsed={onQuestionUsed}
            />
          )}
        </Box>
      </Paper>
      
      {/* Error notification */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clearError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Connection status */}
      {currentUser && (
        <Snackbar
          open={showConnectionStatus}
          autoHideDuration={3000}
          onClose={handleCloseConnectionStatus}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseConnectionStatus} severity="info" sx={{ width: '100%' }}>
            Conectado como: {currentUser.full_name || currentUser.email}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

ChatContainer.propTypes = {
  /** Title to display at the top of the chat container */
  title: PropTypes.string,
  /** Welcome message to show when chat is initialized */
  welcomeMessage: PropTypes.string,
  /** Question to pre-fill in the chat input */
  selectedQuestion: PropTypes.string,
  /** Callback when the selected question has been used */
  onQuestionUsed: PropTypes.func
};

ChatContainer.defaultProps = {
  title: '¿Necesitas saber algo? ¡Estoy aquí para ayudarte!',
  welcomeMessage: '¡Hola! Soy el Agente Ualabi, tu asistente especialista en documentación técnica. ¿En qué puedo ayudarte hoy?',
  selectedQuestion: '',
  onQuestionUsed: () => {}
};

export default ChatContainer;
